import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Repeat, Trash2, Plus, Loader2, Lock, TrendingUp, Zap, ShoppingCart, BarChart3, Heart, MessageCircle, Share2 } from "lucide-react";

export const EcomFunnel = ({ workspaceId }) => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [ever, setEver] = useState(null);
  const [hook, setHook] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showHook, setShowHook] = useState(false);
  const [draft, setDraft] = useState({ keyword: "", checkout_url: "", dm_message: "", platform: "instagram", recovery_enabled: false, recovery_hours: 24, recovery_message: "" });
  const [busy, setBusy] = useState("");

  const load = () => {
    if (!workspaceId) return;
    api.get(`/dm-rules?workspace_id=${workspaceId}`).then((r) => setRules(r.data)).catch(() => {});
    api.get(`/evergreen/status?workspace_id=${workspaceId}`).then((r) => setEver(r.data)).catch(() => {});
    api.get(`/dm-webhook-info?workspace_id=${workspaceId}`).then((r) => setHook(r.data)).catch(() => {});
    api.get(`/evergreen/insights?workspace_id=${workspaceId}`).then((r) => setInsights(r.data)).catch(() => {});
  };
  useEffect(load, [workspaceId]);

  const hasAccess = ever?.has_access;

  const saveRule = async () => {
    if (!draft.keyword.trim() || !draft.checkout_url.trim()) {
      toast.error("Add a keyword and a checkout link.");
      return;
    }
    setBusy("rule");
    try {
      await api.post("/dm-rules", { workspace_id: workspaceId, ...draft });
      toast.success(`Rule for "${draft.keyword}" saved.`);
      setDraft({ keyword: "", checkout_url: "", dm_message: "", platform: "instagram", recovery_enabled: false, recovery_hours: 24, recovery_message: "" });
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Could not save rule.");
    }
    setBusy("");
  };

  const deleteRule = async (id) => {
    await api.delete(`/dm-rules/${id}`).catch(() => {});
    load();
  };

  const toggleEvergreen = async (enabled) => {
    setBusy("evergreen");
    try {
      await api.post("/evergreen/toggle", { workspace_id: workspaceId, enabled });
      setEver((e) => ({ ...e, enabled }));
      toast.success(enabled ? "Evergreen Recycler on." : "Evergreen Recycler off.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Could not update.");
    }
    setBusy("");
  };

  const runEvergreen = async () => {
    setBusy("run");
    try {
      const r = await api.post("/evergreen/run-now", { workspace_id: workspaceId });
      toast.success(r.data.recycled ? `Re-queued ${r.data.recycled} top performer(s) 60 days out.` : "No eligible top performers yet — publish a few posts first.");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Could not run.");
    }
    setBusy("");
  };

  const UpgradeNote = () => (
    <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="ecom-upgrade-note">
      <Lock className="h-3.5 w-3.5 shrink-0" />
      Available on E-commerce Growth &amp; Agency.
      <button className="underline ml-1" onClick={() => navigate("/billing")} data-testid="ecom-upgrade-link">Upgrade</button>
    </div>
  );

  return (
    <>
      {/* Comment-to-DM Sales Funnel */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="dm-funnel-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Comment-to-DM sales funnel</h3>
          <Badge className="bg-secondary text-foreground border-border text-[10px]">Auto-DM tracked checkout links</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          When a follower comments a keyword (e.g. <code className="text-accent">SHOP</code>) on your posts, we instantly DM them a
          pre-filled, UTM-tracked checkout link through your publish webhook.
        </p>
        {!hasAccess && ever && <UpgradeNote />}

        {rules.length > 0 && (
          <div className="space-y-2" data-testid="dm-rules-list">
            {rules.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/50 px-3 py-2" data-testid={`dm-rule-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Badge className="bg-primary/15 text-primary border-primary/30 text-[10px]">{r.platform}</Badge>
                    “{r.keyword}”
                    <span className="text-xs text-muted-foreground">→ {r.triggers_count || 0} sent</span>
                    {r.recovery_enabled && <Badge className="bg-accent/15 text-accent border-accent/30 text-[10px]"><ShoppingCart className="h-3 w-3 mr-1" />recovery {r.recovery_hours || 24}h</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-md">{r.checkout_url}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => deleteRule(r.id)} data-testid={`dm-rule-delete-${r.id}`} className="text-rose-400 hover:text-rose-400 hover:bg-rose-500/10 shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input data-testid="dm-keyword" value={draft.keyword} onChange={(e) => setDraft((d) => ({ ...d, keyword: e.target.value }))} placeholder="Keyword e.g. SHOP" />
          <Input data-testid="dm-checkout-url" value={draft.checkout_url} onChange={(e) => setDraft((d) => ({ ...d, checkout_url: e.target.value }))} placeholder="https://store.com/cart/…" className="sm:col-span-2" />
          <Select value={draft.platform} onValueChange={(v) => setDraft((d) => ({ ...d, platform: v }))}>
            <SelectTrigger data-testid="dm-platform"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["instagram", "facebook", "linkedin", "twitter", "all"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Textarea data-testid="dm-message" value={draft.dm_message} onChange={(e) => setDraft((d) => ({ ...d, dm_message: e.target.value }))} placeholder="DM message (the tracked link is appended automatically)" rows={2} />

        <div className="rounded-lg border border-border bg-background/50 p-3 space-y-2" data-testid="dm-recovery">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium flex items-center gap-1.5"><ShoppingCart className="h-3.5 w-3.5 text-primary" /> Cart recovery follow-up</span>
            <Switch data-testid="dm-recovery-toggle" checked={!!draft.recovery_enabled} disabled={!hasAccess} onCheckedChange={(v) => setDraft((d) => ({ ...d, recovery_enabled: v }))} />
          </div>
          <p className="text-[11px] text-muted-foreground">Re-ping shoppers who tapped the link but didn't buy.</p>
          {draft.recovery_enabled && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Wait</span>
                <Input data-testid="dm-recovery-hours" type="number" min={1} value={draft.recovery_hours}
                  onChange={(e) => setDraft((d) => ({ ...d, recovery_hours: Number(e.target.value) || 24 }))} className="w-20 h-8" />
                <span className="text-xs text-muted-foreground">hours after the tap</span>
              </div>
              <Textarea data-testid="dm-recovery-message" value={draft.recovery_message} onChange={(e) => setDraft((d) => ({ ...d, recovery_message: e.target.value }))} placeholder="Follow-up message (link appended automatically)" rows={2} />
            </div>
          )}
        </div>

        <Button data-testid="dm-rule-save" onClick={saveRule} disabled={busy === "rule" || !hasAccess} className="bg-primary hover:bg-primary/90 text-white h-9 text-sm">
          {busy === "rule" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Add trigger
        </Button>

        {hasAccess && hook && (
          <div className="rounded-lg border border-border bg-background/50 p-3 space-y-2" data-testid="dm-webhook-info">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Your inbound comment webhook</span>
              <button className="text-xs text-primary underline" data-testid="dm-webhook-reveal" onClick={() => setShowHook((s) => !s)}>{showHook ? "Hide" : "Reveal"}</button>
            </div>
            <p className="text-[11px] text-muted-foreground">Point your Zernio / Meta / n8n comment trigger here. Send the token as header <code className="text-accent">X-Webhook-Token</code>.</p>
            <div className="font-mono text-[11px] break-all text-foreground/80">
              <div>POST {hook.webhook_url}</div>
              <div>token: {showHook ? hook.token : "•".repeat(24)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Evergreen Performance Recycler */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="evergreen-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /> Evergreen performance recycler</h3>
          <Switch data-testid="evergreen-toggle" checked={!!ever?.enabled} disabled={!hasAccess || busy === "evergreen"} onCheckedChange={toggleEvergreen} />
        </div>
        <p className="text-xs text-muted-foreground">
          Automatically re-queues your top ~10% performing posts 60 days out with a freshly generated caption.
          Inventory-aware — sold-out products are skipped.
        </p>
        {!hasAccess && ever && <UpgradeNote />}

        {ever?.top_performers?.length > 0 && (
          <div className="space-y-2" data-testid="evergreen-performers">
            <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Top performers</div>
            {ever.top_performers.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-xs rounded-md border border-border bg-background/50 px-3 py-1.5">
                <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">{p.score}</Badge>
                <span className="truncate flex-1">{p.post_text}</span>
                {p.metrics && (p.metrics.likes > 0 || p.metrics.comments > 0) && (
                  <span className="flex items-center gap-2 text-[10px] text-muted-foreground shrink-0" data-testid={`performer-metrics-${p.id}`}>
                    <span className="flex items-center gap-0.5"><Heart className="h-3 w-3 text-rose-400" /> {p.metrics.likes || 0}</span>
                    <span className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3 text-sky-400" /> {p.metrics.comments || 0}</span>
                  </span>
                )}
                {p.recycled_at && <span className="text-emerald-400 shrink-0">re-queued</span>}
              </div>
            ))}
          </div>
        )}

        {hasAccess && (
          <Button data-testid="evergreen-run-now" onClick={runEvergreen} disabled={busy === "run"} variant="outline" className="border-primary/40 text-primary h-9 text-sm">
            {busy === "run" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />} Recycle winners now
          </Button>
        )}
        {ever?.last_run && <p className="text-[11px] text-muted-foreground">Last run {new Date(ever.last_run).toLocaleString()}</p>}
      </div>

      {/* Winner Insights */}
      {insights && insights.total_recycled > 0 && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="winner-insights-card">
          <h3 className="font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Winner insights</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border bg-background/50 p-3 text-center" data-testid="insight-total">
              <div className="text-2xl font-display font-bold">{insights.total_recycled}</div>
              <div className="text-[11px] text-muted-foreground">posts recycled</div>
            </div>
            <div className="rounded-lg border border-border bg-background/50 p-3 text-center" data-testid="insight-avg">
              <div className="text-2xl font-display font-bold">{insights.avg_source_score}</div>
              <div className="text-[11px] text-muted-foreground">avg score</div>
            </div>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-center" data-testid="insight-best">
              <div className="text-2xl font-display font-bold text-emerald-400">{insights.best?.source_score ?? "—"}</div>
              <div className="text-[11px] text-muted-foreground">best performer</div>
            </div>
          </div>
          <div className="space-y-2">
            {insights.items.slice(0, 6).map((it) => {
              const pct = Math.min(100, Math.round((it.source_score / (insights.best?.source_score || 100)) * 100));
              return (
                <div key={it.id} className="space-y-1" data-testid={`insight-item-${it.id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate flex-1 text-foreground/80">{it.post_text}</span>
                    {it.metrics && (it.metrics.likes > 0 || it.metrics.comments > 0) ? (
                      <span className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground shrink-0" data-testid={`insight-metrics-${it.id}`}>
                        <span className="flex items-center gap-0.5"><Heart className="h-3 w-3 text-rose-400" /> {it.metrics.likes || 0}</span>
                        <span className="flex items-center gap-0.5"><MessageCircle className="h-3 w-3 text-sky-400" /> {it.metrics.comments || 0}</span>
                        {it.metrics.shares > 0 && <span className="flex items-center gap-0.5"><Share2 className="h-3 w-3 text-emerald-400" /> {it.metrics.shares}</span>}
                      </span>
                    ) : (
                      <span className="font-mono text-muted-foreground shrink-0">{it.source_score}</span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">Ranked by the original post's engagement score (Zernio analytics when available, else the AI hook score).</p>
        </div>
      )}
    </>
  );
};
